import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, Calendar, ArrowRight, ChevronLeft, Home, Pin, ExternalLink } from 'lucide-react';
import { supabase } from '../supabase/client';
import { formatDate } from '../utils/timeUtils';

interface NoticeItem {
  id: number;
  title: string;
  summary: string;
  content: string;
  link: string;
  link_type: 'internal' | 'external';
  category: string;
  is_pinned: boolean;
  is_highlighted: boolean;
  is_visible: boolean;
  publish_time: string;
}

export default function Notice() {
  const navigate = useNavigate();
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    fetchNotices();
  }, [filter, page]);

  const fetchNotices = async () => {
    let query = supabase
      .from('homepage_notices')
      .select('*', { count: 'exact' })
      .eq('is_visible', true)
      .is('deleted_at', null)
      .order('is_pinned', { ascending: false })
      .order('publish_time', { ascending: false })
      .range((page - 1) * pageSize, page * pageSize - 1);

    if (filter !== 'all') {
      query = query.eq('category', filter);
    }

    const { data, count } = await query;
    if (data) setNotices(data as NoticeItem[]);
    if (count) setTotal(Math.ceil((count || 0) / pageSize));
  };

  const categories = ['考试报名', '政策资讯', '系统公告', '活动通知'];

  const handleNoticeClick = (notice: NoticeItem) => {
    if (notice.link_type === 'external') {
      window.open(notice.link, '_blank');
    } else {
      navigate(`/notice-detail/${notice.id}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-blue-600">
              <ChevronLeft className="w-5 h-5" />
              返回
            </Link>
            <h1 className="text-xl font-bold text-gray-900">通知公告</h1>
          </div>
        </div>
      </header>

      <section className="bg-gradient-to-br from-amber-500 via-amber-600 to-orange-700 text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-2">赛事通知</h2>
          <p className="text-amber-100">最新考试资讯与政策动态</p>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                filter === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >
              全部
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === cat ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {notices.map((notice, index) => (
              <motion.div
                key={notice.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleNoticeClick(notice)}
                className={`bg-white rounded-xl border p-6 cursor-pointer hover:shadow-lg transition-shadow ${
                  notice.is_highlighted ? 'border-red-300 bg-red-50' : 'border-gray-100'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    notice.is_pinned ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
                  }`}>
                    {notice.link_type === 'external' ? <ExternalLink className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                        {notice.category || '通知'}
                      </span>
                      {notice.is_pinned && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                          <Pin className="w-3 h-3" /> 置顶
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-sm text-gray-400">
                        <Calendar className="w-4 h-4" />
                        {formatDate(notice.publish_time)}
                      </span>
                    </div>
                    <h3 className={`text-lg font-bold mb-2 ${notice.is_highlighted ? 'text-red-600' : 'text-gray-900'}`}>
                      {notice.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">{notice.summary}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              </motion.div>
            ))}
          </div>

          {total > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: total }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`px-4 py-2 rounded-lg ${
                    page === p ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
